import { Box, Grid, MenuItem, TextField, Typography } from '@material-ui/core'
import React, { useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import { FormValues } from '../../../lib/cart/types'
import { formFieldLabelText } from '../../../lib/cart/utils'
import { useSelectableTokenBaseCards } from '../../../lib/cybersource/cards'
import CreditCardLogo from '../../CreditCardLogo'
import { RequiredTextField } from '../../form/RequiredFields'

const StyledEllipsisBlock = styled(Typography)`
  display: block;
  text-overflow: ellipsis;
  overflow: hidden;
`

const StyledCreditCardLogo = styled(CreditCardLogo)`
  display: inline-block;
  height: 1em;
  vertical-align: -0.125em;
`

const StyledTitle = styled.h4`
  color: ${({ theme }) => theme.colors.coolGray.cool600};
  font-weight: 400;
  line-height: 28px;
  margin: 0;
`

const StyledAlternative = styled.h4`
  color: ${({ theme }) => theme.colors.coolGray.cool600};
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  margin: 10px 0;
  text-align: center;
`

const SelectSavedCreditCard: React.FC = () => {
  const { data: creditCards } = useSelectableTokenBaseCards()
  const { control, errors } = useFormContext<FormValues.SavedCreditCard>()
  const selectedCreditCardId = useWatch<FormValues.SavedCreditCard>({
    name: 'selectedSavedCreditCardId',
  })

  const options = useMemo(
    () =>
      creditCards?.map((creditCard) => (
        <MenuItem key={creditCard.hash} value={creditCard.hash}>
          <Box>
            <Box fontWeight="bold">
              <StyledEllipsisBlock>
                <StyledCreditCardLogo ccType={creditCard.additional.cc_type} />{' '}
                {`xxxx-${creditCard.additional.cc_last4}`}{' '}
                <Typography variant="caption" component="span">
                  Exp. {creditCard.additional.cc_exp_month}/{creditCard.additional.cc_exp_year}
                </Typography>
              </StyledEllipsisBlock>
            </Box>
            <StyledEllipsisBlock>
              <Typography variant="caption" component="span">
                {creditCard.address.firstname} {creditCard.address.lastname},{' '}
                {creditCard.address.street?.join(' ')}, {creditCard.address.city}{' '}
                {creditCard.address.region?.region_code} {creditCard.address.postcode}
              </Typography>
            </StyledEllipsisBlock>
          </Box>
        </MenuItem>
      )),
    [creditCards],
  )

  if (!creditCards?.length) {
    return null
  }

  return (
    <>
      <Grid item xs={12}>
        <StyledTitle>Select a Card</StyledTitle>
      </Grid>
      <Grid item xs={12}>
        <Controller
          as={
            <TextField
              error={!!errors.selectedSavedCreditCardId}
              fullWidth
              InputProps={{ disableUnderline: true }}
              label={formFieldLabelText({
                error: errors.selectedSavedCreditCardId,
                label: 'Choose Saved Credit Card...',
              })}
              select
              variant="filled"
            >
              <MenuItem value="">Add a Card</MenuItem>
              {options}
            </TextField>
          }
          control={control}
          defaultValue=""
          name="selectedSavedCreditCardId"
        />
      </Grid>
      {selectedCreditCardId ? (
        <Grid item xs={12}>
          <RequiredTextField
            autoComplete="cc-csc"
            inputMode="numeric"
            label="CVV"
            name="cardCVV"
            optionalRules={{ maxLength: 4, minLength: 3 }}
          />
        </Grid>
      ) : (
        <Grid item xs={12}>
          <StyledAlternative>– or –</StyledAlternative>
        </Grid>
      )}
    </>
  )
}

export default SelectSavedCreditCard
